import api from '@shared/services/api';
import { User } from '@shared/types/user';
import { GettingStartedSectionData, EventSectionData, HomePageData, LeaderboardSectionData, QuoteSectionData, RecordingsSectionData } from '../types/home.types';

/**
 * Interface for a single metric item
 */
export interface MetricItem {
  value: string;
  label: string;
}

/**
 * Interface for metrics data shown on the home page
 */
export type MetricsData = MetricItem[];

// HomePageData is now imported from '../types/home.types'

/**
 * Service for handling home page related API calls
 */
export class HomeService {
  private static BASE_URL = '/home';
  /**
   * Fetch all home page data including metrics and user profile
   * @returns Promise with home page data
   */
  static async getHomePageData(): Promise<HomePageData> {
    const response = await api.get<{ data: HomePageData }>(`${this.BASE_URL}/data`);
    return response.data.data;
  }

  /**
   * Update Getting Started section data
   * @param data Updated Getting Started section data
   * @returns Promise with updated Getting Started section data
   */
  static async updateGettingStartedSectionData(data: Partial<GettingStartedSectionData>): Promise<GettingStartedSectionData> {
    const response = await api.put<{ data: GettingStartedSectionData }>(`${this.BASE_URL}/getting-started`, data);
    return response.data.data;
  }

  /**
   * Update Event section data
   * @param data Updated Event section data
   * @returns Promise with updated Event section data
   */
  static async updateEventSectionData(data: Partial<EventSectionData>): Promise<EventSectionData> {
    const response = await api.put<{ data: EventSectionData }>(`${this.BASE_URL}/event-section`, data);
    return response.data.data;
  }

  /**
   * Update Quote section data
   * @param data Updated Quote section data
   * @returns Promise with updated Quote section data
   */
  static async updateQuoteSectionData(data: Partial<QuoteSectionData>): Promise<QuoteSectionData> {
    const response = await api.put<{ data: QuoteSectionData }>(`${this.BASE_URL}/quote-section`, data);
    return response.data.data;
  }

  /**
   * Update Recordings section data
   * @param data Updated Recordings section data
   * @returns Promise with updated Recordings section data
   */
  static async updateRecordingsSectionData(data: Partial<RecordingsSectionData>): Promise<RecordingsSectionData> {
    const response = await api.put<{ data: RecordingsSectionData }>(`${this.BASE_URL}/recordings-section`, data);
    return response.data.data;
  }

  /**
   * Update Leaderboard section data
   * @param data Updated Leaderboard section data
   * @returns Promise with updated Leaderboard section data
   */
  static async updateLeaderboardSectionData(data: Partial<LeaderboardSectionData>): Promise<LeaderboardSectionData> {
    const response = await api.put<{ data: LeaderboardSectionData }>(`${this.BASE_URL}/leaderboard-section`, data);
    return response.data.data;
  }
};
